import React, { useEffect, useRef, useState } from 'react'
import Step from '../../components/Step'
import ButtonSelect from '../../elements/ButtonSelect'
import Resi from '../Resi'
import { fieldName, enforce } from '../HelpersResi'
import Input from '../../elements/Input'
import Select from '../../elements/Select'
import ButtonContainer from '../../elements/ButtonContainer'
import Button from '../../elements/Button'
import ScrollWhenAppears from '../../elements/ScrollWhenAppears'
import {
    AMOUNT_INVALID,
    EXCESS_DUPLICATE,
    EXCESS_ENTRY_REQUIRED,
    EXCESS_ROWS_REQUIRED,
    OTHER_EXCESS_CODE,
    YES_NO_OPTIONS,
    claimExcessTypeOptions,
    excessTypeByCode,
    excessTypeLabel,
    formatDollar,
    hasExcessType,
    parseAmount,
    toExcessHistoryDetail,
    ExcessHistoryDetailApi,
} from '../simplifiedQuoting'


const excessHistory = fieldName('excessHistory')
const excessHistoryDetails = fieldName('excessHistoryDetails')

export const rules = {
    [excessHistory]: [
        enforce.required('Required'),
    ],
    [excessHistoryDetails]: [
        enforce.hasAtLeastOneEntry(EXCESS_ROWS_REQUIRED),
    ],
}


export default () => {
    const navigation = Resi.useNavigation()
    const fields = Resi.useFields()
    const options = Resi.useOptions()
    const errors = Resi.useErrors()

    const typeOptions = claimExcessTypeOptions(options.data?.product?.DOD_CLAIM_EXCESS_TYPE)
    const rows = (fields.value(excessHistoryDetails) || []) as ExcessHistoryDetailApi[]
    const answer = fields.value(excessHistory)
    const selected = answer === true || answer === false ? answer : null

    const [entryType, setEntryType] = useState('')
    const [entryOther, setEntryOther] = useState('')
    const [entryAmount, setEntryAmount] = useState<number | ''>('')
    const [entryError, setEntryError] = useState('')

    const rowError = errors.of(excessHistoryDetails)
    const rowErrorRef = useRef<HTMLDivElement>(null)
    const scroll = Resi.useScroll()

    useEffect(() => {
        if (rowError && rowError.length > 0 && rowErrorRef.current) {
            scroll.ifNoneAbove(rowErrorRef, 26)
        }
    }, [rowError])

    function onSelected(value: boolean) {
        if (value === false) {
            fields.setValues({
                excessHistory: false,
                excessHistoryDetails: [],
            })
            errors.setErrorsForField(excessHistoryDetails, [])
            navigation.goToNext()
            return
        }
        fields.setValue(excessHistory)(true)
    }

    function onAdd() {
        setEntryError('')
        const amount = parseAmount(entryAmount)
        const match = excessTypeByCode(entryType)
        if (!entryType || Number.isNaN(amount) || (entryType === OTHER_EXCESS_CODE && entryOther.trim() === '')) {
            setEntryError(EXCESS_ENTRY_REQUIRED)
            return
        }
        if (amount <= 0) {
            setEntryError(AMOUNT_INVALID)
            return
        }
        if (match && hasExcessType(rows, match.apiType)) {
            setEntryError(EXCESS_DUPLICATE)
            return
        }

        const detail = toExcessHistoryDetail(entryType, entryOther, amount)
        if (!detail) {
            setEntryError(EXCESS_ENTRY_REQUIRED)
            return
        }

        fields.setValues({
            excessHistory: true,
            excessHistoryDetails: [...rows, detail],
        })
        errors.setErrorsForField(excessHistoryDetails, [])
        setEntryType('')
        setEntryOther('')
        setEntryAmount('')
    }

    function onRemove(index: number) {
        fields.setValue(excessHistoryDetails)(rows.filter((_, rowIndex) => rowIndex !== index))
    }

    function onContinue() {
        const current = (fields.value(excessHistoryDetails) || []) as ExcessHistoryDetailApi[]
        if (current.length === 0) {
            errors.setErrorsForField(excessHistoryDetails, [EXCESS_ROWS_REQUIRED])
            return
        }
        errors.setErrorsForField(excessHistoryDetails, [])
        navigation.goToNext()
    }

    return (
        <Step>
            <h2>Has your current policy had any additional excesses imposed?</h2>
            <ButtonSelect
                options={YES_NO_OPTIONS}
                selected={selected}
                colsLg={3}
                colsSm={1}
                callback={onSelected}
                sort={false}
            />
            <ScrollWhenAppears condition={selected === true}>
                <div className="dod-history">
                    <h4>Please provide details</h4>
                    <div className="dod-entry-width dod-entry-pair">
                        <Select
                            label="Excess Type"
                            field={[entryType, setEntryType]}
                            options={typeOptions}
                            onSelectChange={(value) => {
                                if (value !== OTHER_EXCESS_CODE) setEntryOther('')
                                setEntryError('')
                            }}
                        />
                        <Input
                            field={[entryAmount, setEntryAmount]}
                            type="dollar"
                            label="Imposed Excess Amount"
                        />
                    </div>
                    {entryType === OTHER_EXCESS_CODE &&
                        <div className="dod-entry-width">
                            <Input
                                field={[entryOther, setEntryOther]}
                                type="text"
                                label="Other Excess Type"
                                placeholder="Other Excess Name"
                                maxLength={100}
                            />
                        </div>
                    }
                    {entryError && <div className="dod-history-error">{entryError}</div>}
                    <ButtonContainer>
                        <Button label="Add" type="outline-secondary" callback={onAdd} />
                    </ButtonContainer>

                    {rows.length > 0 &&
                        <div className="dod-history-list dod-entry-width">
                            <h4>Previously Imposed Excesses</h4>
                            {rows.map((row, index) => (
                                <div className="dod-history-entry" key={`${row.type}-${index}`}>
                                    <div className="dod-history-fields">
                                        <div className="main-label">Excess Type</div>
                                        <div>{excessTypeLabel(row, typeOptions)}</div>
                                        <div className="main-label">Imposed Excess Amount</div>
                                        <div>{formatDollar(row.amount)}</div>
                                    </div>
                                    <button type="button" className="btn btn-outline-secondary dod-remove" aria-label="Remove" onClick={() => onRemove(index)}>
                                        <i className="fas fa-trash" aria-hidden="true" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    }
                    {rowError && rowError.length > 0 && <div className="dod-history-error" ref={rowErrorRef}>{rowError[0]}</div>}
                    <ButtonContainer>
                        <Button label="Continue" type="primary" icon="fas fa-chevron-right" callback={onContinue} />
                    </ButtonContainer>
                </div>
            </ScrollWhenAppears>
        </Step>
    )
}
